import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MAT_SELECT_CONFIG, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';
import { CustomerPropertyCodes } from '@hub4b/constantes/customer-property-codes';
import { Customer } from '@hub4b/models/server/customer';
import { CustomersService } from '@hub4b/services/http/customers.service';
import { MatSelectInfiniteScrollModule } from 'ng-mat-select-infinite-scroll';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { FolderPropertyCodes } from 'src/app/core/constantes/folder-property-codes';
import { SortDirections } from 'src/app/core/constantes/sort-directions';
import { DateHelper } from 'src/app/helpers/date-helper';
import { ColumnType } from 'src/app/models/enums/column-type';
import { FilterType } from 'src/app/models/enums/filter-type';
import { PropertyType } from 'src/app/models/enums/property-type';
import { Box } from 'src/app/models/server/box';
import { FilterInfos } from 'src/app/models/server/core/filter-infos';
import { SortInfos } from 'src/app/models/server/core/sort-infos';
import { Folder } from 'src/app/models/server/folder';
import { FolderState } from 'src/app/models/server/folder-state';
import { SpinnerService } from 'src/app/services/core/spinner.service';
import { FolderStatesService } from 'src/app/services/http/folder-states.service';
import { FoldersService } from 'src/app/services/http/folders.service';
import { BoxesStateService } from 'src/app/services/state/boxes-state.service';
import { ViewsStateService } from 'src/app/services/state/views-state.service';
import { FormFocusDirective } from '../../core/directives/form-focus.directive';

@Component({
    selector: 'app-folder-selector',
    templateUrl: './folder-selector.component.html',
    styleUrls: ['./folder-selector.component.scss'],
    providers: [
        {
            provide: MAT_SELECT_CONFIG,
            useValue: { overlayPanelClass: 'hub4b-select-overlay-pane' },
        },
    ],
    imports: [
        MatFormFieldModule,
        MatSelectModule,
        MatSelectInfiniteScrollModule,
        MatTooltipModule,
        FaIconComponent,
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        MatOptionModule,
        NgxSpinnerModule,
        DatePipe,
    ]
})
export class FolderSelectorComponent implements OnInit, OnDestroy {
    faXmark = faXmark;
    faMagnifyingGlass = faMagnifyingGlass;

    /**
     * Références des constantes utilisées dans le template.
     */
    public FRENCH_DATETIME_FORMAT = DateHelper.FRENCH_DATETIME_FORMAT;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Liste des dossiers
     */
    public folders: Folder[];

    /**
     * Liste des bannettes de l'organisation.
     */
    public boxes: Box[];

    /**
     * Liste des états que peut prendre un dossier.
     */
    private folderStates: FolderState[];

    /**
     * Liste des clients.
     */
    private customers: Customer[];

    /**
     * Valeur du filtre courant.
     */
    currentSortInfos: SortInfos = { columnName: FolderPropertyCodes.NAME, direction: SortDirections.DESC, columnType: PropertyType.Text };

    /**
     * Filtre lié à la recherche.
     */
    searchFilterInfos: FilterInfos[] = [];

    /**
     * Nombre d'enregistements à retourner à chaque requête.
     */
    pageSize: number = 50;

    /**
     * Index du premier dossier à retourner lors de requête avec pagination.
     */
    from: number = 0;

    /**
     * Résultat de la recherche.
     */
    public searchResult: Folder = null;

    /**
     * Liste des dossiers à ne pas afficher dans la liste déroulante
     */
    @Input()
    public ignoredFolders: Folder[];

    /**
     * Emis lorsqu'un dossier est sélectionné.
     */
    @Output()
    public folderSelected: EventEmitter<Folder> = new EventEmitter();

    /**
     * CFormulaire de recherche.
     */
    public folderSearchForm: UntypedFormGroup;

    /**
     * Constructeur
     * @param foldersService Service de gestion des dossiers
     * @param datePipe Service de gestion des dates
     */
    constructor(
        private foldersService: FoldersService,
        private folderStateService: FolderStatesService,
        private customersService: CustomersService,
        private viewsStateService: ViewsStateService,
        private boxesStateService: BoxesStateService,
        private datePipe: DatePipe,
        protected cdRef: ChangeDetectorRef,
        private formBuilder: UntypedFormBuilder,
        private spinner: SpinnerService
    ) {}

    /**
     * Initialise le composant
     */
    ngOnInit(): void {
        if (!!!this.ignoredFolders) {
            this.ignoredFolders = [];
        }

        this.loadFolderStates();
        this.loadCustomers();
        // this.loadBoxes();
        this.loadFolders();

        this.folderSearchForm = this.formBuilder.group({
            folderSearch: [''],
        });

        this.folderSearchForm.controls['folderSearch'].valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((value) => {
            this.onSearch(value);
        });
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Envenement lors qu'on arrive à la fin du scroll de la liste déroulante
     */
    public onScroll(): void {
        this.loadFolders();
    }

    /**
     * Appelé lorsque l'utilisateur selectionne un dossier.
     * @param folder Dossier selectionné.
     */
    public onSelected(folder: Folder): void {
        this.searchResult = folder;
        this.folderSelected.emit(folder);
    }

    /**
     * Appelé lorsque l'utilisateur saisi du texte dans le champ de recherche.
     */
    public onSearch(searchValue: string): void {
        this.folders = [];
        this.from = 0;
        this.searchFilterInfos = [];
        this.searchFilterInfos.push({
            columnName: FolderPropertyCodes.NAME,
            value: searchValue,
            type: ColumnType.Text,
            filterType: FilterType.Text,
            selectionMultiple: false,
        });
        this.loadFolders();
    }

    /**
     * Appelé lorsque l'utilisateur clique sur le bouton Reset de la zone de recherche.
     * */
    public onReset(): void {
        this.folderSearchForm.controls['folderSearch'].setValue('');
        this.searchFilterInfos = [];
        this.folders = [];
        this.from = 0;
        this.loadFolders();
    }

    /**
     * Charge les dossiers.
     */
    private loadFolders(): void {
        this.spinner.show('select-folder-spinner');
        this.foldersService
            .folderList(this.viewsStateService.getSelectedView().id, this.from, this.pageSize, this.currentSortInfos, [], this.searchFilterInfos, true)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((result) => {
                if (this.from == 0) {
                    this.folders = [];
                }

                result.value
                    .sort((a, b) => {
                        if (a.name.toLowerCase() > b.name.toLowerCase()) {
                            return 1;
                        } else {
                            return -1;
                        }
                    })
                    .forEach((folder) => {
                        if (!!!this.ignoredFolders.find((f) => f.id === folder.id)) {
                            this.folders.push(folder);
                        }
                    });

                this.completeFolders();

                this.from += this.pageSize;
                this.spinner.hide('select-folder-spinner');
            });
    }

    /**
     * Complete chaque dossier avec le code de son état et le nom de la bannette dans lequel il se trouve.
     */
    private completeFolders() {
        if (this.folders && this.folderStates && this.customers) {
            this.folders.forEach((folder) => {
                // if (folder.boxId && !folder['box']) {
                // 	folder['box'] = this.boxes.find((x) => x.id == folder.boxId)?.name;
                // }

                if (folder.folderStateId && !folder['state']) {
                    folder['state'] = this.folderStates.find((x) => x.id == folder.folderStateId)?.name;
                }

                if (folder.customerId && !folder['customerName']) {
                    folder['customerName'] = this.customers.find((x) => x.id == folder.customerId)?.name;
                }
            });

            //this.cdRef.detectChanges();
        }
    }

    /**
     * Charge les bannettes de l'organisation.
     */
    // private loadBoxes() {
    // 	this.boxes = this.boxesStateService.getBoxesList();
    // }

    /**
     * Charge les différentes valeurs d'états que peut prendre un dossier.
     */
    private loadFolderStates() {
        this.folderStateService.list().subscribe((x) => {
            this.folderStates = x;
        });
    }

    /**
     * Charge les la liste des clients.
     */
    private loadCustomers() {
        const sortInfo: SortInfos = { columnName: CustomerPropertyCodes.NAME, direction: SortDirections.ASC, columnType: PropertyType.Text };

        this.customersService.List(null, null, sortInfo).subscribe((x) => {
            this.customers = x.value;
        });
    }
}
